import { CHANNEL_TYPE } from "../../Data/SD";
import UserInfo from "../../interfaces/UserInfo";
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { GetHeaders } from "../videoService/VideoService";

export interface MeetingRoomDto {
    id: string;
    roomId: string;
    calendarEventId?: string;
    createdAt: Date;
    startDate: Date;
    title?: string;
}

export interface OngoingCallDto {
    roomId: string;
    startTime: Date;
    status?: string;
    callRecordId?: number;
}

export interface Conversation {
    id: number;
    conversationId?: number; // For unified API response (maps to id)
    isBotDirect?: boolean;
    participants: UserInfo[];
    lastMessage?: Message;
    lastReadAt?: Date;
    unreadCount?: number;
    channel?: ChannelDto;
    activeMeeting?: MeetingRoomDto;
    ongoingCall?: OngoingCallDto;
    isChannel?: boolean; // Flag to distinguish channels from conversations
}

export interface ChannelDto {
    channelId: string;
    conversationId: number;
    name: string;
    topic: string | null;
    channelType: CHANNEL_TYPE;
    associatedEntityId: string | null;
    ownerId: string | null;

    /**
     * Channel state returned by the conversation endpoints.
     *
     * These remain optional during the API rollout so older channel payloads
     * and any existing ChannelDto object literals continue to compile.
     */
    isArchived?: boolean;
    canWrite?: boolean;
}

export interface RemoveAdHocFromConversationDto {
    conversationId: number;
    roomId: string;
}

export interface Reaction {
    fullName: string; 
    userId: string;
    reactionType: string
}

export interface ChatActivity {
    id: number;
    activityType: "chat.reaction" | "chat.thread_reply" | string;
    actorUserId: string;
    actorDisplayName: string;
    conversationId: number;
    messageId: number;
    threadRootMessageId?: number | null;
    reactionType?: string | null;
    summary: string;
    createdAt: string;
    read: boolean;
}

export interface ChatReadState {
    conversationId: number;
    lastVisibleMessageId: number;
    threadRootMessageId?: number | null;
    readActivityIds: number[];
    readAt: string;
}

export async function markChatReadThrough(
    authToken: string,
    conversationId: number,
    lastVisibleMessageId: number,
    threadRootMessageId?: number,
): Promise<ChatReadState> {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/${conversationId}/read-through`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ lastVisibleMessageId, threadRootMessageId }),
    });
    if (!response.ok) throw new Error(await response.text() || "Unable to update chat read state.");
    return response.json();
}

export async function getRecentChatActivity(authToken: string, page = 1, pageSize = 30): Promise<ChatActivity[]> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/recent-activity?page=${page}&pageSize=${pageSize}`,
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } },
    );
    if (!response.ok) throw new Error("Unable to load recent chat activity.");
    return response.json();
}

export async function markChatActivityRead(authToken: string, activityId: number): Promise<void> {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/recent-activity/${activityId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) throw new Error("Unable to mark chat activity as read.");
}

/** Marks each activity read; rejects when any individual request fails. */
export async function markChatActivitiesRead(authToken: string, activityIds: number[]): Promise<void> {
    const results = await Promise.allSettled(activityIds.map(activityId => markChatActivityRead(authToken, activityId)));
    if (results.some(result => result.status === "rejected")) throw new Error("Unable to mark chat activity as read.");
}

export async function markChatActivityForMessageRead(authToken: string, messageId: number): Promise<void> {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/recent-activity/message/${messageId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) throw new Error("Unable to mark message activity as read.");
}

export async function markAllChatActivityRead(authToken: string): Promise<void> {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/recent-activity/read-all`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) throw new Error("Unable to mark chat activity as read.");
}

export interface Message {
    attachmentUrl: any;
    id: number;
    clientMessageId?: string;
    conversationId: number;
    author: UserInfo | null;
    messageContent: string;
    createDate: Date;
    attachmentType: string;
    attachmentOriginalName?: string | null;
    attachmentContentType?: string | null;
    attachmentSize?: number | null;
    attachmentScanStatus?: string | null;
    messageType: string;
    isBot?: boolean;
    systemEventType?: string | null;
    systemEventData?: unknown;
    reactions?: Reaction[];
    isNew?: boolean,
    isOptimistic?: boolean;
    deliveryStatus?: "queued" | "sending" | "retrying" | "failed" | "sent";
    clientOrder?: number;
    optimisticAttachmentName?: string;
    isDeleted?: boolean,
    isEdited?: boolean,
    callRecordId?: number;
    lastReadAt: number;
    contentFormat?: number;
    messageDoc?: any;
    messagePlaintext?: string;
    parentMessageId?: number;
    threadRootMessageId?: number;
    threadReplyCount?: number;
    isBookmarked?: boolean;
}

export interface ChatMessageSearchFilters {
    query?: string;
    authorId?: string;
    filesOnly?: boolean;
    bookmarkedOnly?: boolean;
    includeThreads?: boolean;
    pageIndex?: number;
    pageSize?: number;
}

const unwrapChatData = <T,>(payload: any, fallback: T): T =>
    payload?.data !== undefined ? payload.data as T : (payload as T ?? fallback);

export async function searchConversationMessages(
    authToken: string,
    conversationId: number,
    filters: ChatMessageSearchFilters,
): Promise<Message[]> {
    const params = new URLSearchParams();
    if (filters.query?.trim()) params.set("query", filters.query.trim());
    if (filters.authorId) params.set("authorId", filters.authorId);
    if (filters.filesOnly) params.set("filesOnly", "true");
    if (filters.bookmarkedOnly) params.set("bookmarkedOnly", "true");
    params.set("includeThreads", String(filters.includeThreads ?? true));
    params.set("pageIndex", String(filters.pageIndex ?? 1));
    params.set("pageSize", String(filters.pageSize ?? 20));

    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/search/conversation/${conversationId}?${params}`,
        { headers: GetHeaders(authToken, true) },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.message || "Unable to search this conversation.");
    return unwrapChatData<Message[]>(payload, []);
}

export interface GlobalChatSearchFilters {
    query?: string;
    conversationId?: number;
    filesOnly?: boolean;
    includeThreads?: boolean;
    pageIndex?: number;
    pageSize?: number;
}

export async function searchMyChatMessages(
    authToken: string,
    filters: GlobalChatSearchFilters,
): Promise<Message[]> {
    const params = new URLSearchParams();
    if (filters.query?.trim()) params.set("query", filters.query.trim());
    if (filters.conversationId) params.set("conversationId", String(filters.conversationId));
    if (filters.filesOnly) params.set("filesOnly", "true");
    params.set("includeThreads", String(filters.includeThreads ?? true));
    params.set("pageIndex", String(filters.pageIndex ?? 1));
    params.set("pageSize", String(filters.pageSize ?? 30));
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/search/messages?${params}`,
        { headers: GetHeaders(authToken, true) },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.message || "Unable to search your messages.");
    return unwrapChatData<Message[]>(payload, []);
}

export async function getConversationMessageAuthors(
    authToken: string,
    conversationId: number,
): Promise<UserInfo[]> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/search/conversation/${conversationId}/authors`,
        { headers: GetHeaders(authToken, true) },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.message || "Unable to load message senders.");
    return unwrapChatData<UserInfo[]>(payload, []);
}

export async function toggleChatBookmark(authToken: string, messageId: number): Promise<boolean> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/message/${messageId}/bookmark`,
        { method: "POST", headers: GetHeaders(authToken, true) },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.message || "Unable to update the saved message.");
    return unwrapChatData<boolean>(payload, false);
}

export async function getMessageContext(authToken: string, conversationId: number, messageId: number, before = 30, after = 30): Promise<Message[]> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/${conversationId}/messages/context/${messageId}?before=${before}&after=${after}`,
        { headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" } },
    );
    if (!response.ok) throw new Error("Unable to load the linked message.");
    return response.json();
}

// export interface SendMessageRequest {
//     conversationId: number;
//     text: string;
//     attachmentUrl: string;
// }

interface CreateConversationRequest {
    participantIds: string[];
}

const toDate = (value: unknown, fallback: Date = new Date()): Date => {
    if (value instanceof Date) return value;

    if (typeof value === "string" || typeof value === "number") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    return fallback;
};

const normalizeMeetingRoom = (raw: any): MeetingRoomDto | undefined => {
    if (!raw) return undefined;

    const roomId = raw.roomId ?? raw.RoomId ?? "";
    const startValue =
        raw.startDate ?? raw.StartDate ?? raw.startTime ?? raw.StartTime ?? raw.createdAt ?? raw.CreatedAt;
    const createdValue =
        raw.createdAt ?? raw.CreatedAt ?? raw.startTime ?? raw.StartTime ?? startValue;

    return {
        id: String(raw.id ?? raw.Id ?? raw.callRecordId ?? raw.CallRecordId ?? roomId ?? ""),
        roomId: String(roomId ?? ""),
        calendarEventId: raw.calendarEventId ?? raw.CalendarEventId,
        createdAt: toDate(createdValue),
        startDate: toDate(startValue),
        title: raw.title ?? raw.Title,
    };
};

const normalizeOngoingCall = (raw: any): OngoingCallDto | undefined => {
    if (!raw) return undefined;

    const roomId = raw.roomId ?? raw.RoomId;
    if (!roomId) return undefined;

    return {
        roomId: String(roomId),
        startTime: toDate(raw.startTime ?? raw.StartTime),
        status: raw.status ?? raw.Status,
        callRecordId: raw.callRecordId ?? raw.CallRecordId,
    };
};

/**
 * Normalize every chat endpoint into one frontend conversation contract.
 *
 * A currently-running OngoingCall is the most authoritative join target and
 * therefore takes precedence over an upcoming/scheduled ActiveMeeting.
 */
export const normalizeConversation = (raw: any): Conversation => {
    const ongoingCall = normalizeOngoingCall(raw?.ongoingCall ?? raw?.OngoingCall);
    const scheduledMeeting = normalizeMeetingRoom(raw?.activeMeeting ?? raw?.ActiveMeeting);

    const liveMeeting = ongoingCall
        ? normalizeMeetingRoom({
            id: ongoingCall.callRecordId,
            roomId: ongoingCall.roomId,
            startTime: ongoingCall.startTime,
            title: "Call in progress",
        })
        : undefined;

    return {
        ...raw,
        id: raw?.conversationId ?? raw?.ConversationId ?? raw?.id ?? raw?.Id,
        participants: raw?.participants ?? raw?.Participants ?? [],
        lastMessage: raw?.lastMessage ?? raw?.LastMessage,
        lastReadAt: raw?.lastReadAt ?? raw?.LastReadAt,
        unreadCount: raw?.unreadCount ?? raw?.UnreadCount,
        channel: raw?.channel ?? raw?.Channel,
        isChannel: raw?.isChannel ?? raw?.IsChannel,
        isBotDirect: raw?.isBotDirect ?? raw?.IsBotDirect,
        ongoingCall,
        activeMeeting: liveMeeting ?? scheduledMeeting,
    } as Conversation;
};

/**
 * Gets all conversations for the authenticated user.
 * Auth token is a Firebase or JWT token
 */
export async function getMyConversations(
    authToken: string,
    includeMeetings: boolean = true // Added parameter
): Promise<Conversation[]> {

    // Append query parameter if requested
    const url = `${process.env.REACT_APP_API_BASE_URL}/chat/my-conversations${includeMeetings ? '?includeMeetings=true' : ''}`;

    const response = await fetch(
        url,
        {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        }
    );
    if (!response.ok) {
        throw new Error("Failed to fetch conversations");
    }
    const payload = await response.json();
    const conversations = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
            ? payload.data
            : [];

    return conversations.map(normalizeConversation);
}

/**
 * Gets all conversations for the authenticated user.
 * Auth token is a Firebase or JWT token
 */
export async function GetMyChannelConversationAsync(
    authToken: string,
    userId: string
): Promise<ApiResponse<Conversation[]>> {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/chat/user/${userId}/get-myChannels`,
            {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            }
        );

        return response.json();
    } catch (error) {
        throw error;
    }
}

/**
 * Gets conversations for Two Users.
 * Auth token is a Firebase or JWT token
 */
export async function GetConversationsofTwoUser(
    authToken: string,
    targetedUserId: string
): Promise<ApiResponse<Conversation>> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/privatechats/${targetedUserId}/conversation`,
        {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        }
    );
    if (!response.ok) {
        console.log("Failed to fetch conversations");
    }

    const data = await response.json();
    console.log("response: ", data);
    return data;
}

/**
 * Gets all messages for a particular conversation
 */
export async function getMessagesForConversation(
    authToken: string,
    conversationId: number,
    pageSize: number = 50,
    beforeMessageId?: number
): Promise<Message[]> {

    let url = `${process.env.REACT_APP_API_BASE_URL}/chat/${conversationId}/messages/pagination?pageSize=${pageSize}`;

    if (beforeMessageId) {
        url += `&beforeMessageId=${beforeMessageId}`;
    }
    const response = await fetch(
        url,
        {
            headers: GetHeaders(authToken),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to fetch messages");
    }
    const message = await response.json()
    return message;
}

export async function getThreadReplies(
    authToken: string,
    threadRootMessageId: number
): Promise<Message[]> {
    const url = `${process.env.REACT_APP_API_BASE_URL}/chat/thread/${threadRootMessageId}`;
    const response = await fetch(url, {
        headers: GetHeaders(authToken),
    });

    if (!response.ok) {
        throw new Error("Failed to fetch thread replies");
    }
    const message = await response.json();
    return message;
}

export async function getConversationById(
    authToken: string,
    conversationId: number
): Promise<Conversation | null> {
    try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/conversation/${conversationId}`;
        const response = await fetch(apiUrl, {
            headers: GetHeaders(authToken),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch conversation ${conversationId}: ${response.status}`);
        }

        const payload = await response.json();
        const rawConversation = payload?.data ?? payload;

        return rawConversation ? normalizeConversation(rawConversation) : null;
    } catch (error) {
        console.error("Failed to fetch conversation by ID:", error);
        return null;
    }
}

/**
 * Sends a message to a user (this will create a conversation if not exists)
 */
// export async function sendMessage(
//     authToken: string,
//     conversationId: number,
//     text: string,
//     attachmentUrl?: string
// ): Promise<{ conversationId: number; messageId: number; attachmentUrl?: string }> {
//     console.log('Attachment URL being sent to API: ', attachmentUrl);

//     const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/chat/send`,
//         {
//             method: "POST",
//             headers: GetHeaders(authToken, true),
//             body: JSON.stringify({ conversationId, text, attachmentUrl }),
//         }
//     );

//     if (!response.ok) {
//         const errorText = await response.text();
//         throw new Error(`Failed to send message: ${errorText}`);
//     }

//     const message = await response.json();
//     console.log('Message', message)
//     return message;
// }

export interface SendMessageRequest {
    conversationId: number;
    text: string;
    attachmentUrl?: string | undefined; // Made this optional based on your backend
    doc?: any;              // NEW: TipTap JSON document
    parentMessageId?: number; // NEW: For threading later
}

export async function sendMessage(
    authToken: string,
    conversationId: number,
    text: string,
    doc?: any,               // NEW
    attachmentUrl?: string,
    parentMessageId?: number, // NEW
    clientMessageId?: string,
): Promise<Message> {

    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/send`,
        {
            method: "POST",
            headers: GetHeaders(authToken, true),
            body: JSON.stringify({
                conversationId,
                text,
                doc,             // NEW
                attachmentUrl,
                parentMessageId, // NEW
                clientMessageId,
            }),
        }
    );

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Message send failed (${response.status}): ${errorText || response.statusText}`);
    }

    const message = await response.json();
    return message;
}

// -------------- CREATE CONVERSATION --------------
//
// Creating a conversation is a state-changing operation, so cancellation is not
// enough to protect against duplicate clicks: by the time AbortController aborts
// locally, the server may already have accepted and committed the first request.
//
// Instead, use a "single-flight" request keyed by the exact participant set.
// While one create request for a participant set is in flight, every caller gets
// the SAME Promise instead of starting another HTTP request.
//
// The API remains the authoritative concurrency boundary (it protects against
// other tabs, browsers, devices, and servers); this guard prevents duplicate
// requests from this frontend runtime and gives the UI a stable Promise to await.
const createConversationInFlight = new Map<
    string,
    Promise<ApiResponse<Conversation>>
>();

const getConversationParticipantKey = (participantIds: string[]): string =>
    Array.from(new Set(participantIds.filter(Boolean)))
        .sort((a, b) => a.localeCompare(b))
        .join("\u001F");

export const createConversation = (
    token: string,
    participantIds: string[],
    callerScope = ""
): Promise<ApiResponse<Conversation>> => {
    const normalizedParticipantIds = Array.from(
        new Set(participantIds.filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    // Token is an in-memory isolation key only; never logged or persisted.
    const requestKey = JSON.stringify([token, localStorage.getItem("impersonatedUserId") ?? "", callerScope, getConversationParticipantKey(normalizedParticipantIds)]);
    const existingRequest = createConversationInFlight.get(requestKey);

    if (existingRequest) {
        return existingRequest;
    }

    const request = (async (): Promise<ApiResponse<Conversation>> => {
        const url = `${process.env.REACT_APP_API_BASE_URL}/chat/create-conversation`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                participantIds: normalizedParticipantIds,
            } as CreateConversationRequest),
        });

        const data = await response.json();

        if (!response.ok) {
            const message =
                data?.message ??
                `Failed to create conversation (${response.status})`;
            throw new Error(message);
        }

        return data;
    })();

    createConversationInFlight.set(requestKey, request);

    const clearRequest = () => {
        if (createConversationInFlight.get(requestKey) === request) {
            createConversationInFlight.delete(requestKey);
        }
    };

    // Use then(success, failure) rather than creating an ignored rejecting
    // Promise via finally().
    void request.then(clearRequest, clearRequest);

    return request;
};

// -------------- DELETE CONVERSATION --------------
export const deleteConversationAsync = async (
    token: string,
    conversationId: number
): Promise<ApiResponse<string>> => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/chat/delete/conversation/${conversationId}`;

    const response = await fetch(
        url,
        {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        }
    );

    const data = await response.json();
    return data;
};

export async function markConversationAsRead(
    authToken: string,
    conversationId: number
): Promise<ApiResponse<any>> {
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/chat/${conversationId}/mark-as-read`,
        {
            method: "POST",
            headers: GetHeaders(authToken, true),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to mark conversation as read");
    }

    // Check if response has content before parsing JSON
    const text = await response.text();

    // If response body is empty, return a default success response
    if (!text || text.trim() === "") {
        return {
            data: null,
            statusCode: response.status,
            message: "Conversation marked as read",
            success: true,
        };
    }

    // Response has content, parse it as JSON
    try {
        return JSON.parse(text);
    } catch (error) {
        // If parsing fails, return a default success response
        return {
            data: null,
            statusCode: response.status,
            message: "Conversation marked as read",
            success: true,
        };
    }
}

export async function getAllConversations(
    authToken: string,
    includeMeetings: boolean = true,
    includeArchived: boolean = true
): Promise<ApiResponse<Conversation[]>> {
    try {
        const params = new URLSearchParams();
        if (includeMeetings) {
            params.append('includeMeetings', 'true');
        }
        params.append('includeArchived', String(includeArchived));

        const queryString = params.toString();
        const url = `${process.env.REACT_APP_API_BASE_URL}/chat/all${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });

        // Handle 403 Permission Denied for archived channels
        if (response.status === 403) {
            const errorData = await response.json().catch(() => ({ message: 'Permission Denied' }));
            return {
                data: [],
                statusCode: 403,
                message: errorData.message || 'You don\'t have permission to view archived channels',
                success: false,
            };
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch conversations: ${errorText}`);
        }

        const responseData = await response.json();

        // Handle both ApiResponse format and direct data format
        let conversations: any[] = [];
        let apiResponse: ApiResponse<Conversation[]>;

        if (responseData.data && Array.isArray(responseData.data)) {
            // Standard ApiResponse format
            conversations = responseData.data;
            apiResponse = {
                data: [],
                statusCode: responseData.statusCode || response.status,
                message: responseData.message || 'Success',
                success: responseData.success !== undefined ? responseData.success : true,
            };
        } else if (Array.isArray(responseData)) {
            // Direct array format (fallback)
            conversations = responseData;
            apiResponse = {
                data: [],
                statusCode: response.status,
                message: 'Success',
                success: true,
            };
        } else {
            throw new Error('Unexpected response format');
        }

        // Normalize both persisted meeting shapes (ActiveMeeting + OngoingCall)
        // into the activeMeeting contract consumed by the chat UI.
        apiResponse.data = conversations.map((conv: any) => {
            const normalizedConv: any = normalizeConversation(conv);

            if (normalizedConv.lastMessage) {
                const lastMsg = normalizedConv.lastMessage;

                if (!lastMsg.author && lastMsg.userId && normalizedConv.participants) {
                    const authorParticipant = normalizedConv.participants.find(
                        (participant: UserInfo) => participant.id === lastMsg.userId
                    );
                    if (authorParticipant) {
                        normalizedConv.lastMessage = {
                            ...lastMsg,
                            author: authorParticipant,
                        };
                    }
                }

                if (!normalizedConv.lastMessage.messageType) {
                    normalizedConv.lastMessage.messageType = "USER";
                }

                if (!normalizedConv.lastMessage.reactions) {
                    normalizedConv.lastMessage.reactions = [];
                }

                if (normalizedConv.lastMessage.attachmentType === undefined) {
                    normalizedConv.lastMessage.attachmentType = "";
                }
            }

            return normalizedConv;
        });

        return apiResponse;
    } catch (error: any) {
        return {
            data: [],
            statusCode: 500,
            message: error.message || 'Failed to fetch conversations',
            success: false,
        };
    }
}
